import { Loader2, MonitorDown, Pencil, Rocket } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import { fmtDateTime } from '../shared/format'
import type { PanelRelease } from '../shared/types'
import { MAX_NOTES, VERSION_RE } from '../updates/rules'
import { usePanelReleases, usePublishPanelRelease, useUpdatePanelRelease } from './api'

function EditPanelModal({ release, onClose }: { release: PanelRelease; onClose: () => void }) {
  const { t } = useTranslation()
  const update = useUpdatePanelRelease()
  const [notes, setNotes] = useState(release.notes)
  const [minServer, setMinServer] = useState(release.min_server_version)
  const minOk = minServer.trim() === '' || VERSION_RE.test(minServer.trim())

  const submit = async () => {
    try {
      await update.mutateAsync({ id: release.id, notes: notes.trim(), min_server_version: minServer.trim() })
      toast.success(t('releases.panel.saved'))
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!update.isPending}
      icon={<Pencil />}
      title={t('releases.panel.editTitle')}
      description={`${release.account_title} · ${release.version}`}
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!minOk || update.isPending}>
            {update.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('releases.panel.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="panel-min-server">{t('releases.panel.minServer')}</Label>
          <Input
            id="panel-min-server"
            placeholder="1.5.0"
            value={minServer}
            onChange={(e) => setMinServer(e.target.value)}
            aria-invalid={!minOk}
          />
          <p className="text-muted-foreground text-xs">{t('releases.panel.minServerHint')}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="panel-notes">{t('releases.panel.notes')}</Label>
          <Textarea id="panel-notes" rows={6} maxLength={MAX_NOTES} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
    </Modal>
  )
}

/** Müşteriye özel masaüstü panel sürümleri (klinik ADR-0024 §4): CI'ın yüklediği taslaklar burada düzeltilir ve yayımlanır. */
export function PanelReleasesCard({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation()
  const panels = usePanelReleases()
  const publish = usePublishPanelRelease()
  const [editing, setEditing] = useState<PanelRelease | null>(null)
  const [publishing, setPublishing] = useState<PanelRelease | null>(null)

  const confirmPublish = async () => {
    if (!publishing) return
    try {
      await publish.mutateAsync(publishing.id)
      toast.success(t('releases.panel.published', { version: publishing.version }))
      setPublishing(null)
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  return (
    <Card className="mt-6">
      <div className="flex items-start gap-3 border-b p-4">
        <MonitorDown className="text-muted-foreground mt-0.5 size-5 shrink-0" />
        <div>
          <h2 className="text-sm font-semibold">{t('releases.panel.title')}</h2>
          <p className="text-muted-foreground text-xs">{t('releases.panel.hint')}</p>
        </div>
      </div>
      {panels.isError ? (
        <ErrorState error={panels.error} onRetry={() => void panels.refetch()} />
      ) : (
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('releases.panel.account')}</TH>
              <TH>{t('releases.panel.version')}</TH>
              <TH className="hidden sm:table-cell">{t('releases.panel.minServer')}</TH>
              <TH>{t('releases.panel.packages')}</TH>
              <TH>{t('releases.panel.state')}</TH>
              <TH className="text-right">{t('releases.columns.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {panels.isLoading
              ? Array.from({ length: 3 }, (_, i) => (
                  <TR key={i}>
                    <TD colSpan={6}>
                      <Skeleton className="h-6" />
                    </TD>
                  </TR>
                ))
              : (panels.data ?? []).map((p) => (
                  <TR key={p.id}>
                    <TD className="max-w-48 truncate">{p.account_title}</TD>
                    <TD className="font-medium" title={p.notes || undefined}>
                      {p.version}
                    </TD>
                    <TD className="text-muted-foreground hidden sm:table-cell">{p.min_server_version || '—'}</TD>
                    <TD>
                      {p.packages.length === 0 ? (
                        <span className="text-muted-foreground text-xs">{t('releases.panel.noPackage')}</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {p.packages.map((pkg) => (
                            <Badge key={pkg.platform} tone="neutral" title={`${pkg.file_name} · ${pkg.sha256}`}>
                              {pkg.platform}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TD>
                    <TD>
                      {p.published_at ? (
                        <span className="text-muted-foreground text-xs tabular-nums">{fmtDateTime(p.published_at)}</span>
                      ) : (
                        <Badge tone="warning">{t('releases.panel.draft')}</Badge>
                      )}
                    </TD>
                    <TD className="text-right">
                      {canWrite && !p.published_at ? (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                            <Pencil />
                            {t('releases.panel.edit')}
                          </Button>
                          <Button size="sm" disabled={p.packages.length === 0} onClick={() => setPublishing(p)}>
                            <Rocket />
                            {t('releases.panel.publish')}
                          </Button>
                        </div>
                      ) : null}
                    </TD>
                  </TR>
                ))}
          </TBody>
        </Table>
      )}
      {!panels.isLoading && !panels.isError && (panels.data ?? []).length === 0 ? (
        <EmptyState title={t('releases.panel.empty')} />
      ) : null}
      {editing ? <EditPanelModal release={editing} onClose={() => setEditing(null)} /> : null}
      <ConfirmModal
        open={!!publishing}
        onOpenChange={(v) => !v && setPublishing(null)}
        title={t('releases.panel.publish')}
        description={publishing ? t('releases.panel.publishConfirm', { version: publishing.version }) : undefined}
        confirmLabel={t('releases.panel.publish')}
        loading={publish.isPending}
        onConfirm={confirmPublish}
      />
    </Card>
  )
}
